// Articles API service for browsing news articles imported from Forge CMS.

import { api } from './api'

export type ArticleStage = 'draft' | 'published'

export interface MarkdownPart {
  _type: 'markdown_part'
  content: string
}

export interface PhotoPart {
  _type: 'photo_part'
  tc_image_id: string
}

export interface ExternalPart {
  _type: 'external_part'
  provider?: string | null
  url?: string | null
  html?: string | null
}

export interface VideoEmbedPart {
  _type: 'video_embed_part'
  video_id: string
  player_id: string
  title?: string | null
  description?: string | null
  duration?: number | null
}

export interface UnknownPart {
  _type: string
  [key: string]: unknown
}

export type ArticlePart =
  | MarkdownPart
  | PhotoPart
  | ExternalPart
  | VideoEmbedPart
  | UnknownPart

export interface Article {
  tc_article_id: string
  site_id: string | null
  stage: ArticleStage
  published_at: string | null
  content_date: string | null
  word_count: number | null
  title: string | null
  headline: string | null
  byline: string | null
}

export interface ResolvedImage {
  tc_image_id: string
  cdn_url: string | null
  caption: string | null
  alt_text: string | null
  credit: string | null
  pixel_width: number | null
  pixel_height: number | null
}

export interface ArticleDetail extends Article {
  source: string
  source_id: string
  language_code: string | null
  parts: ArticlePart[]
  resolved_images: Record<string, ResolvedImage>
}

export interface PhotoImageMetadataUpdate {
  alt_text?: string | null
  credit?: string | null
}

export interface ArticleUpdatePayload {
  title?: string
  headline?: string | null
  byline?: string | null
  parts?: ArticlePart[]
  stage?: ArticleStage
  photo_image_metadata?: Record<string, PhotoImageMetadataUpdate>
}

export interface ArticlesPagination {
  page: number
  limit: number
  total_count: number
  total_pages: number
  has_next: boolean
  has_prev: boolean
}

export interface ArticlesResponse {
  articles: Article[]
  pagination: ArticlesPagination
  filters: Record<string, unknown>
  timestamp: string
}

export interface ArticleFilters {
  search?: string
  start_date?: string
  end_date?: string
  stage?: ArticleStage | ''
  site_id?: string
  language?: string
  page?: number
  limit?: number
}

export const fetchArticles = (filters?: ArticleFilters): Promise<ArticlesResponse> => {
  const params: Record<string, any> = {}
  if (filters?.search?.trim()) params.search = filters.search.trim()
  if (filters?.start_date) params.start_date = filters.start_date
  if (filters?.end_date) params.end_date = filters.end_date
  if (filters?.stage) params.stage = filters.stage
  if (filters?.site_id) params.site_id = filters.site_id
  if (filters?.language) params.language = filters.language
  if (filters?.page) params.page = filters.page
  if (filters?.limit) params.limit = filters.limit

  return api.get<ArticlesResponse>('/articles', params)
}

export const fetchArticle = (
  articleId: string,
  language: string = 'en',
): Promise<ArticleDetail> =>
  api.get<ArticleDetail>(`/articles/${articleId}`, { language })

export const createArticle = (
  body: { site_id: string; title?: string },
  language: string = 'en',
): Promise<ArticleDetail> =>
  api.post<ArticleDetail>(`/articles?language=${encodeURIComponent(language)}`, body)

export const updateArticle = (
  articleId: string,
  body: ArticleUpdatePayload,
  language: string = 'en',
): Promise<ArticleDetail> =>
  api.put<ArticleDetail>(`/articles/${articleId}?language=${encodeURIComponent(language)}`, body)

export const uploadArticleImage = (
  articleId: string,
  file: File,
): Promise<ResolvedImage> => {
  const formData = new FormData()
  formData.append('file', file)
  return api.upload<ResolvedImage>(`/articles/${articleId}/images`, formData)
}
